import { useCallback } from 'react'
import store from '../../../../store'
import { requireSave } from '../../../../store/requiredSaveData'
import { SprintDetail } from '../../../../lib/functions/sprint'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  SprintProductItemDetail,
  createSprintProductItemKey,
  addSprintToRow,
  removeSprintOfRow,
  isAssociatedWithSomeSprint,
} from '../productBacklog'
import { SprintAdjustmentItem } from '../components/SprintAssignmentDialog'
import { moveRowsToLastChild } from '../../../containers/BulkSheetView/hooks/actions/moveTreeRows'
import {
  createUnsetSprintPbiRow,
  insertCopiedRowForMultiSprint,
} from './sprintOperations'

interface UseSprintAdjustmentProps {
  sprints: SprintDetail[]
  data: ProductBacklogRow[]
  setData: (data: ProductBacklogRow[]) => void
  incrementDataVersion: () => void
}

/** 変更の分類結果 */
interface ClassifiedChanges {
  sprintsToAdd: string[]
  sprintsToRemove: string[]
  hourChangeMap: Map<string, number>
  hasHourChange: boolean
}

/** 新規追加行の編集マーカーを設定（名称列用） - 行固有のキーを使用 */
const setEditMarkerForNewRow = (
  editedData: Record<string, unknown>,
  rowUuid: string
) => {
  editedData[`sprintChanged.${rowUuid}`] = true
}

/** 変更内容を分類する */
const classifyChanges = (
  items: SprintAdjustmentItem[],
  currentSprintUuids: Set<string>,
  currentHourMap: Map<string, number>
): ClassifiedChanges => {
  const sprintsToAdd: string[] = []
  const sprintsToRemove: string[] = []
  const hourChangeMap = new Map<string, number>()
  let hasHourChange = false

  for (const item of items) {
    const wasChecked = currentSprintUuids.has(item.sprintUuid)
    if (item.checked && !wasChecked) {
      sprintsToAdd.push(item.sprintUuid)
    } else if (!item.checked && wasChecked) {
      sprintsToRemove.push(item.sprintUuid)
    }
    if (item.checked) {
      hourChangeMap.set(item.sprintUuid, item.estimatedHour)
      const currentHour = currentHourMap.get(item.sprintUuid) ?? 0
      if (item.estimatedHour !== currentHour) {
        hasHourChange = true
      }
    }
  }

  return { sprintsToAdd, sprintsToRemove, hourChangeMap, hasHourChange }
}

/** スプリント削除処理 */
const processRemovals = (
  targetPbiRows: ProductBacklogItemRow[],
  sprintsToRemove: string[],
  sprintMapByUuid: Record<string, SprintDetail>
): { rowUuidsToRemove: Set<string>; editedPbiRow?: ProductBacklogItemRow } => {
  const rowUuidsToRemove = new Set<string>()
  let editedPbiRow: ProductBacklogItemRow | undefined

  for (const sprintUuid of sprintsToRemove) {
    const rowToRemove = targetPbiRows.find(
      row => row.treeValue[0] === sprintUuid
    )
    if (rowToRemove) {
      const parentSprint = sprintMapByUuid[sprintUuid]
      if (parentSprint) {
        removeSprintOfRow(rowToRemove, parentSprint)
        editedPbiRow = rowToRemove
      }
      rowUuidsToRemove.add(rowToRemove.uuid)
    }
  }

  return { rowUuidsToRemove, editedPbiRow }
}

/** スプリント追加処理 */
const processAdditions = (
  targetPbiRows: ProductBacklogItemRow[],
  representativeRow: ProductBacklogItemRow,
  sprintsToAdd: string[],
  sprintMapByUuid: Record<string, SprintDetail>
): {
  targetPbiRowsBeforeUnsetSprint: ProductBacklogItemRow[]
  newlyAddedRowUuids: Set<string>
} => {
  const targetPbiRowsBeforeUnsetSprint: ProductBacklogItemRow[] = []
  const newlyAddedRowUuids = new Set<string>()

  for (const sprintUuid of sprintsToAdd) {
    const targetSprint = sprintMapByUuid[sprintUuid]
    if (!targetSprint) continue

    const alreadyAdded = targetPbiRows.some(
      row => row.treeValue[0] === sprintUuid
    )
    if (alreadyAdded) continue

    addSprintToRow(representativeRow, targetSprint)

    if (!isAssociatedWithSomeSprint(representativeRow)) {
      targetPbiRowsBeforeUnsetSprint.push(representativeRow)
      newlyAddedRowUuids.add(representativeRow.uuid)
    }
  }

  return { targetPbiRowsBeforeUnsetSprint, newlyAddedRowUuids }
}

/** 行データ更新関数を生成 */
const createRowDataUpdater = (
  originalSprint: SprintDetail | undefined,
  originalSprints: SprintDetail[],
  hourChangeMap: Map<string, number>,
  newlyAddedRowUuids: Set<string>
) => {
  return (row: ProductBacklogItemRow) => {
    if (!row.editedData) {
      row.editedData = {}
    }

    // スプリント変更をeditedDataに記録
    if (!row.editedData.hasOwnProperty('wbsItem.sprint')) {
      row.editedData['wbsItem.sprint'] = originalSprint
      row.editedData['wbsItem.sprints'] = originalSprints
    }

    const parentSprintUuid = row.treeValue[0]

    // 新規追加行のみに編集マーカーを設定（行固有のキーを使用）
    if (newlyAddedRowUuids.has(row.uuid)) {
      setEditMarkerForNewRow(row.editedData, row.uuid)
    }

    // 見積工数の更新
    const newEstimatedHour = hourChangeMap.get(parentSprintUuid)
    if (newEstimatedHour !== undefined) {
      const currentItem = row.sprintProductItems?.find(
        spi => spi.sprintUuid === parentSprintUuid
      )
      const currentHour = currentItem?.estimatedHour ?? 0

      if (newEstimatedHour !== currentHour) {
        const key = createSprintProductItemKey(parentSprintUuid)
        // editedDataに新しい値を設定（グリッド表示用）
        // getSprintEstimatedHourがeditedDataから値を読み取るため
        row.editedData[key] = newEstimatedHour
      }
    }

    // sprintProductItemsを更新
    const originalItems = row.sprintProductItems || []
    const newSprintProductItems: SprintProductItemDetail[] = []

    for (const [sprintUuid, estimatedHour] of hourChangeMap) {
      const existing = originalItems.find(spi => spi.sprintUuid === sprintUuid)
      newSprintProductItems.push(
        existing
          ? { ...existing, estimatedHour }
          : {
              uuid: '',
              sprintUuid,
              estimatedHour,
              taskEstimatedHour: 0,
              displayOrder: 0,
              lockVersion: 0,
            }
      )
    }

    row.sprintProductItems = newSprintProductItems
    row.edited = true
  }
}

/** データ配列を再構築 */
const rebuildDataArray = (
  data: ProductBacklogRow[],
  originalUuid: string,
  representativeRow: ProductBacklogItemRow,
  rowUuidsToRemove: Set<string>,
  editedPbiRow: ProductBacklogItemRow | undefined,
  sprintsToAdd: string[],
  targetPbiRowsBeforeUnsetSprint: ProductBacklogItemRow[],
  newlyAddedRowUuids: Set<string>,
  updateRowData: (row: ProductBacklogItemRow) => void
): ProductBacklogRow[] => {
  let newData = [...data]

  // 削除対象の行を除去し、編集状態を伝播
  newData = newData.filter(v => {
    if (
      isProductBacklogItemRow(v) &&
      editedPbiRow &&
      v.originalUuid === editedPbiRow.originalUuid
    ) {
      v.edited = editedPbiRow.edited
      v.editedData = editedPbiRow.editedData
    }
    return !rowUuidsToRemove.has(v.uuid)
  })

  // 削除後にスプリント紐付けがなくなった場合、未設定行に追加
  const hasRemainingRows = newData.some(
    row => isProductBacklogItemRow(row) && row.originalUuid === originalUuid
  )

  const pbiRowsNotAssociatedWithSprint: ProductBacklogItemRow[] = []
  if (!hasRemainingRows && sprintsToAdd.length === 0) {
    pbiRowsNotAssociatedWithSprint.push(
      createUnsetSprintPbiRow(representativeRow)
    )
  }

  // 未設定スプリントから移動する行の処理
  if (targetPbiRowsBeforeUnsetSprint.length > 0) {
    newData = moveRowsToLastChild(
      newData,
      targetPbiRowsBeforeUnsetSprint,
      sprintsToAdd[0]
    )
  }

  // コピー行を追加
  const firstSprintToAdd = sprintsToAdd[0]
  for (const sprintUuid of sprintsToAdd) {
    // 最初のスプリントは未設定から移動済みの場合スキップ
    if (
      targetPbiRowsBeforeUnsetSprint.length > 0 &&
      sprintUuid === firstSprintToAdd
    ) {
      continue
    }

    const result = insertCopiedRowForMultiSprint(
      newData,
      sprintUuid,
      representativeRow
    )
    if (result.copiedRow) {
      newlyAddedRowUuids.add(result.copiedRow.uuid)
    }
    newData = result.newData
  }

  // 未設定行を追加
  newData.push(...pbiRowsNotAssociatedWithSprint)

  // 対象行にデータ変更を適用
  newData = newData.map(row => {
    if (isProductBacklogItemRow(row) && row.originalUuid === originalUuid) {
      const updatedRow = { ...row }
      updateRowData(updatedRow)
      return updatedRow
    }
    return row
  })

  return newData
}

export const useSprintAdjustment = ({
  sprints,
  data,
  setData,
  incrementDataVersion,
}: UseSprintAdjustmentProps) => {
  const applySprintAdjustment = useCallback(
    (deliverable: ProductBacklogItemRow, items: SprintAdjustmentItem[]) => {
      const { originalUuid } = deliverable

      // 現在の状態を取得
      const currentSprintUuids = new Set(
        (deliverable.wbsItem.sprints || []).map(s => s.uuid)
      )
      const currentHourMap = new Map(
        (deliverable.sprintProductItems || []).map(spi => [
          spi.sprintUuid,
          spi.estimatedHour,
        ])
      )
      const sprintMapByUuid = Object.fromEntries(sprints.map(s => [s.uuid, s]))

      // 1. 変更を分類
      const { sprintsToAdd, sprintsToRemove, hourChangeMap, hasHourChange } =
        classifyChanges(items, currentSprintUuids, currentHourMap)

      // 変更がない場合は早期リターン
      if (
        sprintsToAdd.length === 0 &&
        sprintsToRemove.length === 0 &&
        !hasHourChange
      ) {
        return false
      }

      // 対象のPBI行を取得
      const targetPbiRows = data.filter(
        (row): row is ProductBacklogItemRow =>
          isProductBacklogItemRow(row) && row.originalUuid === originalUuid
      )

      if (targetPbiRows.length === 0) {
        return false
      }

      const representativeRow = targetPbiRows[0]

      // 2. 削除処理
      const { rowUuidsToRemove, editedPbiRow } = processRemovals(
        targetPbiRows,
        sprintsToRemove,
        sprintMapByUuid
      )

      // 3. 追加処理
      const { targetPbiRowsBeforeUnsetSprint, newlyAddedRowUuids } =
        processAdditions(
          targetPbiRows,
          representativeRow,
          sprintsToAdd,
          sprintMapByUuid
        )

      // 4. 行データ更新関数を生成
      const originalSprint = deliverable.wbsItem.sprint
      const originalSprints = deliverable.wbsItem.sprints
        ? [...deliverable.wbsItem.sprints]
        : []

      const updateRowData = createRowDataUpdater(
        originalSprint,
        originalSprints,
        hourChangeMap,
        newlyAddedRowUuids
      )

      // 5. データ配列を再構築
      const newData = rebuildDataArray(
        data,
        originalUuid,
        representativeRow,
        rowUuidsToRemove,
        editedPbiRow,
        sprintsToAdd,
        targetPbiRowsBeforeUnsetSprint,
        newlyAddedRowUuids,
        updateRowData
      )

      setData(newData)
      incrementDataVersion()
      store.dispatch(requireSave())

      return true
    },
    [sprints, data, setData, incrementDataVersion]
  )

  return { applySprintAdjustment }
}
